'use client';

import React, { useEffect } from 'react';
import styled from '@benzinga/themetron';
import Image from 'next/image';
import { formatTickerToURLFriendly } from '@benzinga/utils';
import { formatPercentage, formatPriceWithCurrency } from '@benzinga/utils/numberUtils';
import type { DetailsQuoteType, RankingDetail } from '@benzinga/quotes-manager';
import { AddToWatchlist } from '@benzinga/watchlist-ui';
import { EdgeRanking } from '@benzinga/ticker-ui';
import { usePermission } from '@benzinga/user-context';

interface StockInfoProps extends StockInfoHeaderProps {
  rankingData?: RankingDetail | null;
  isLoading?: boolean;
}

interface StockInfoHeaderProps {
  symbol: string;
  name: string | null;
  price: number | null;
  changePercent: number | null;
  logoUrl: string | null;
  type: DetailsQuoteType;
  isPrimaryTicker?: boolean;
}

export const StockInfoChart: React.FC<StockInfoProps> = ({
  changePercent,
  isLoading = false,
  isPrimaryTicker = false,
  logoUrl,
  name,
  price,
  rankingData,
  symbol,
  type,
}) => {
  const [show, setShow] = React.useState(false);
  const hasPermission = usePermission('com/read', 'unlimited-calendars');
  const isCrypto = type === 'CRYPTO';
  const rankingSlotHeight = isCrypto ? 112 : 165;

  useEffect(() => {
    if (hasPermission) {
      setShow(true);
    }
  }, [hasPermission]);

  return (
    <Container className="flex flex-col">
      <StockInfoHeader
        changePercent={changePercent}
        isPrimaryTicker={isPrimaryTicker}
        logoUrl={logoUrl}
        name={name}
        price={price}
        symbol={symbol}
        type={type}
      />
      <Divider />
      <div style={{ minHeight: rankingSlotHeight }}>
        {isLoading ? (
          <div
            className="box-border w-full rounded-lg animate-pulse border border-[#5b7292]/30 bg-[#4a5c7a]/40"
            style={{ height: rankingSlotHeight }}
          />
        ) : rankingData && rankingData.exists ? (
          <EdgeRanking
            {...rankingData}
            adType={'stockwidget'}
            isCrypto={isCrypto}
            showRanking={show}
            symbol={symbol}
            variant="dark"
          />
        ) : (
          <div
            className="flex items-center justify-center text-center text-gray-400 text-sm"
            style={{ minHeight: 112 }}
          >
            Benzinga Edge Rankings not available for {name}
          </div>
        )}
      </div>
    </Container>
  );
};

const Divider: React.FC = () => {
  return <div className="border-t border-[#192940] my-3" />;
};

export const StockInfoHeader: React.FC<StockInfoHeaderProps> = ({
  changePercent,
  isPrimaryTicker = false,
  logoUrl,
  name,
  price,
  symbol,
  type,
}) => {
  const changePercentClassname =
    Number(changePercent) === 0
      ? 'bg-[#273851] text-white'
      : Number(changePercent) > 0
        ? 'bg-[#143233] text-green-500'
        : 'bg-[#432734] text-red-500';
  return (
    <a
      className="stock-info-header flex min-w-0 w-full overflow-hidden"
      href={`/quote/${formatTickerToURLFriendly(symbol)}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      {logoUrl && (
        <div className="flex shrink-0 justify-center items-center border-2 border-gray-700 rounded-lg p-1 mr-4 bg-white">
          <Image
            alt={`${symbol} Logo`}
            height={40}
            loader={() => logoUrl}
            src={logoUrl}
            style={{ objectFit: 'contain' }}
            width={40}
          />
        </div>
      )}
      <TickerPriceDetail className="flex min-w-0 flex-1 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span
            className={
              isPrimaryTicker
                ? 'stock-info-symbol inline-flex items-center self-start box-border max-w-full w-fit font-bold text-black rounded px-2 py-0.5 border border-white/35'
                : 'stock-info-symbol text-white'
            }
            style={
              isPrimaryTicker
                ? {
                    background:
                      'linear-gradient(180deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.08) 28%, transparent 50%), #ffff04',
                  }
                : undefined
            }
          >
            {type === 'CRYPTO' && '$'}
            {symbol?.replace('/USD', '')}
          </span>
          <span className="stock-info-name text-gray-500 truncate">{name}</span>
        </div>
        <div className="flex shrink-0 flex-row gap-3 items-center">
          <div className="flex flex-col items-end">
            <span className="text-white text-right whitespace-nowrap">
              {price ? formatPriceWithCurrency(price, 'USD') : 'Not Available'}
            </span>
            <span
              className={`change-percent ${changePercentClassname} font-bold text-center text-xs rounded-full px-2 py-1 mt-0.5 whitespace-nowrap`}
            >
              {changePercent ? `${formatPercentage(changePercent)}` : '-'}%
            </span>
          </div>
          <div className="quick-add-watchlist shrink-0">
            <AddToWatchlist buttonVariant="flat-blue" showIcon={true} symbol={symbol} variant="compact" />
          </div>
        </div>
      </TickerPriceDetail>
    </a>
  );
};

export default StockInfoChart;

const Container = styled.div``;

const TickerPriceDetail = styled.div`
  .quick-add-watchlist {
    button {
      min-width: 40px;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center !important;
      svg {
        width: 1.6em !important;
        height: 1.6em !important;
      }
    }
  }
`;
